import React from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  Paper,
  Avatar,
  Breadcrumbs,
  Link as MuiLink,
  Fade,
  Slide,
  Button,
  Chip
} from '@mui/material';
import {
  NavigateNext as NavigateNextIcon,
  Store as StoreIcon,
  TrendingUp as TrendingUpIcon,
  MonetizationOn as MonetizationOnIcon,
  Support as SupportIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
  Analytics as AnalyticsIcon,
  Star as StarIcon
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import becomeSellerBn from '../i18n/bn/becomeSeller';
import becomeSellerEn from '../i18n/en/becomeSeller';
import becomeSellerNp from '../i18n/np/becomeSeller';
import becomeSellerPk from '../i18n/pk/becomeSeller';
import becomeSellerBt from '../i18n/bt/becomeSeller';
import becomeSellerSl from '../i18n/sl/becomeSeller';
import becomeSellerDe from '../i18n/de/becomeSeller';
import becomeSellerAf from '../i18n/af/becomeSeller';
import becomeSellerCn from '../i18n/cn/becomeSeller';

const BecomeSeller = () => {
  const currentLanguage = (typeof window !== 'undefined' && localStorage.getItem('preferredLanguage')) || 'bn';
  const t = currentLanguage === 'en' ? becomeSellerEn : 
            currentLanguage === 'np' ? becomeSellerNp : 
            currentLanguage === 'pk' ? becomeSellerPk : 
            currentLanguage === 'bt' ? becomeSellerBt : 
            currentLanguage === 'de' ? becomeSellerDe : 
            currentLanguage === 'sl' ? becomeSellerSl : 
            currentLanguage === 'af' ? becomeSellerAf : 
            currentLanguage === 'cn' ? becomeSellerCn : 
            becomeSellerBn;

  const sellerBenefits = [
    {
      icon: <TrendingUpIcon />,
      title: t.benefits.items[0].title,
      description: t.benefits.items[0].description,
      color: '#4CAF50'
    },
    {
      icon: <MonetizationOnIcon />,
      title: t.benefits.items[1].title,
      description: t.benefits.items[1].description,
      color: '#2196F3'
    },
    {
      icon: <SupportIcon />,
      title: t.benefits.items[2].title,
      description: t.benefits.items[2].description,
      color: '#FF9800'
    },
    {
      icon: <SecurityIcon />,
      title: t.benefits.items[3].title,
      description: t.benefits.items[3].description,
      color: '#9C27B0'
    },
    {
      icon: <SpeedIcon />,
      title: t.benefits.items[4].title,
      description: t.benefits.items[4].description,
      color: '#F44336'
    },
    {
      icon: <AnalyticsIcon />,
      title: t.benefits.items[5].title,
      description: t.benefits.items[5].description,
      color: '#607D8B'
    }
  ];

  const sellerStats = [
    {
      icon: <PeopleIcon sx={{ fontSize: 48 }} />,
      value: t.stats[0].value,
      label: t.stats[0].label,
      color: '#4CAF50'
    },
    {
      icon: <MonetizationOnIcon sx={{ fontSize: 48 }} />,
      value: t.stats[1].value,
      label: t.stats[1].label,
      color: '#2196F3'
    },
    {
      icon: <TrendingUpIcon sx={{ fontSize: 48 }} />,
      value: t.stats[2].value,
      label: t.stats[2].label,
      color: '#FF9800'
    },
    {
      icon: <StarIcon sx={{ fontSize: 48 }} />,
      value: t.stats[3].value,
      label: t.stats[3].label,
      color: '#9C27B0'
    }
  ];

  return (
    <>
      <Header />
      <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh' }}>
        {/* Hero Section */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            py: 8,
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          <Container>
            <Breadcrumbs 
              separator={<NavigateNextIcon fontSize="small" />} 
              aria-label="breadcrumb"
              sx={{ mb: 4, '& .MuiBreadcrumbs-separator': { color: 'rgba(255,255,255,0.7)' } }}
            >
              <MuiLink component={Link} to="/" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                {t.breadcrumbs.home}
              </MuiLink>
              <Typography sx={{ color: 'white' }}>{t.breadcrumbs.becomeSeller}</Typography>
            </Breadcrumbs>

            <Fade in timeout={1000}>
              <Box sx={{ textAlign: 'center', mb: 6 }}>
                <Typography variant="h2" component="h1" gutterBottom sx={{ fontWeight: 700, mb: 2 }}>
                  {t.hero.title}
                </Typography>
                <Typography variant="h5" sx={{ opacity: 0.9, maxWidth: '800px', mx: 'auto', mb: 4 }}>
                  {t.hero.subtitle}
                </Typography>
                <Button
                  component={Link}
                  to="/seller-signup"
                  variant="contained"
                  size="large"
                  sx={{
                    bgcolor: 'white',
                    color: 'primary.main',
                    px: 4,
                    py: 1.5,
                    fontWeight: 600,
                    '&:hover': {
                      bgcolor: 'grey.100'
                    }
                  }}
                >
                  {t.hero.cta}
                </Button>
              </Box>
            </Fade>
          </Container>
        </Box>

        <Container sx={{ py: 6 }}>
          {/* Transform Your Dreams Section */}
          <Box sx={{ mb: 8, pt: '80px' }}>
            <Fade in timeout={1200}>
              <Paper
                sx={{
                  p: 6,
                  background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                  borderRadius: 3,
                  border: '2px solid #dee2e6',
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)'
                  }
                }}
              >
                <Box sx={{ textAlign: 'center', mb: 4 }}>
                  <StoreIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
                  <Typography 
                    variant="h3" 
                    component="h2" 
                    gutterBottom 
                    sx={{ 
                      fontWeight: 700, 
                      color: 'primary.main',
                      fontFamily: currentLanguage === 'bn' ? 'SolaimanLipi, Arial, sans-serif' : 'inherit'
                    }}
                  >
                    {t.transformDreams.title}
                  </Typography>
                </Box>

                <Box sx={{ 
                  p: 4, 
                  bgcolor: 'white', 
                  borderRadius: 2, 
                  border: '1px solid #e9ecef',
                  maxWidth: '800px',
                  mx: 'auto'
                }}>
                  {t.transformDreams.content.map((paragraph, index) => (
                    <Typography 
                      key={index}
                      variant="body1" 
                      paragraph 
                      sx={{ 
                        lineHeight: 1.8, 
                        color: 'text.secondary',
                        fontFamily: currentLanguage === 'bn' ? 'SolaimanLipi, Arial, sans-serif' : 'inherit',
                        fontSize: '1.1rem',
                        mb: 3,
                        textAlign: 'justify',
                        '&:last-child': {
                          fontWeight: 500,
                          color: 'primary.main'
                        }
                      }}
                    >
                      {paragraph}
                    </Typography>
                  ))}
                </Box>
              </Paper>
            </Fade>
          </Box>

          {/* Seller Stats */}
          <Box sx={{ mb: 8, pt: '80px' }}>
            <Grid container spacing={4}>
              {sellerStats.map((stat, index) => (
                <Grid item xs={6} md={3} key={index}>
                  <Slide direction="up" in timeout={500 + index * 200}>
                    <Card
                      sx={{
                        textAlign: 'center',
                        p: 3,
                        height: '100%',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-8px)',
                          boxShadow: '0 12px 24px rgba(0,0,0,0.15)'
                        },
                        border: '1px solid #e2e8f0'
                      }}
                    >
                      <Avatar
                        sx={{
                          bgcolor: stat.color,
                          width: 80,
                          height: 80,
                          mx: 'auto',
                          mb: 2
                        }}
                      >
                        {stat.icon}
                      </Avatar>
                      <Typography variant="h3" sx={{ fontWeight: 700, color: stat.color, mb: 1 }}>
                        {stat.value}
                      </Typography>
                      <Typography variant="h6" color="text.secondary">
                        {stat.label}
                      </Typography>
                    </Card>
                  </Slide>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Benefits Section */}
          <Box sx={{ mb: 8, pt: '80px' }}>
            <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 600, textAlign: 'center', mb: 6 }}>
              {t.benefits.title}
            </Typography>
            <Grid container spacing={6} sx={{ px: 2 }}>
              {sellerBenefits.map((benefit, index) => (
                <Grid item xs={12} sm={6} md={4} key={index} sx={{ mb: 4 }}>
                  <Slide direction="up" in timeout={800 + index * 200}>
                    <Card
                      sx={{
                        textAlign: 'center',
                        p: 4,
                        height: '100%',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 8px 24px rgba(0,0,0,0.12)'
                        },
                        border: '1px solid #e2e8f0',
                        borderRadius: 2,
                        mx: 1,
                        mt: 2
                      }}
                    >
                      <Avatar
                        sx={{
                          bgcolor: benefit.color,
                          width: 72,
                          height: 72,
                          mx: 'auto',
                          mb: 3
                        }}
                      >
                        {benefit.icon}
                      </Avatar>
                      <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                        {benefit.title}
                      </Typography>
                      <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7, fontSize: '1rem' }}>
                        {benefit.description}
                      </Typography>
                    </Card>
                  </Slide>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* How it Works */}
          <Box sx={{ mb: 8, pt: '80px' }}>
            <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 600, textAlign: 'center', mb: 6 }}>
              {t.steps.title}
            </Typography>
            <Grid container spacing={4}>
              <Grid item xs={12} md={4}>
                <Fade in timeout={1000}>
                  <Card sx={{ textAlign: 'center', p: 4, height: '100%', border: '1px solid #e2e8f0' }}>
                    <Chip label={t.steps.stepNumbers[0]} color="primary" sx={{ mb: 2, width: 40, height: 40, fontSize: '1.2rem' }} />
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                      {t.steps.items[0].title}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      {t.steps.items[0].description}
                    </Typography>
                  </Card>
                </Fade>
              </Grid>
              <Grid item xs={12} md={4}>
                <Fade in timeout={1200}>
                  <Card sx={{ textAlign: 'center', p: 4, height: '100%', border: '1px solid #e2e8f0' }}>
                    <Chip label={t.steps.stepNumbers[1]} color="primary" sx={{ mb: 2, width: 40, height: 40, fontSize: '1.2rem' }} />
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                      {t.steps.items[1].title}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      {t.steps.items[1].description}
                    </Typography>
                  </Card>
                </Fade>
              </Grid>
              <Grid item xs={12} md={4}>
                <Fade in timeout={1400}>
                  <Card sx={{ textAlign: 'center', p: 4, height: '100%', border: '1px solid #e2e8f0' }}>
                    <Chip label={t.steps.stepNumbers[2]} color="primary" sx={{ mb: 2, width: 40, height: 40, fontSize: '1.2rem' }} />
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                      {t.steps.items[2].title}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      {t.steps.items[2].description}
                    </Typography>
                  </Card>
                </Fade>
              </Grid>
            </Grid>
          </Box>

          {/* Call to Action */}
          <Paper
            sx={{
              p: 6,
              textAlign: 'center',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              borderRadius: 3,
              mt: '80px'
            }}
          >
            <BusinessIcon sx={{ fontSize: 64, mb: 2, opacity: 0.9 }} />
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
              {t.cta.title}
            </Typography>
            <Typography variant="h6" sx={{ mb: 4, opacity: 0.9, maxWidth: '600px', mx: 'auto' }}>
              {t.cta.subtitle}
            </Typography>
            <Button
              component={Link}
              to="/seller-signup"
              variant="contained"
              size="large"
              sx={{
                bgcolor: 'white',
                color: 'primary.main',
                px: 4,
                py: 1.5,
                fontWeight: 600,
                '&:hover': {
                  bgcolor: 'grey.100'
                }
              }}
            >
              {t.cta.button}
            </Button>
          </Paper>
        </Container>
      </Box>
      <Footer />
    </>
  );
};

export default BecomeSeller; 